import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, authHeaders, getApiErrorMessage } from "@/lib/streamcareShared";

const emptyForm = { name: "", active: true };

export function IptvApplicationsSettings() {
  const [apps, setApps] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadApps = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/iptv-applications?include_inactive=true`, { headers: authHeaders() });
      setApps(data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Chargement applications impossible"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadApps(); }, [loadApps]);

  const reset = () => { setForm(emptyForm); setEditingId(null); };
  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return toast.error("Nom d’application requis");
    setSaving(true);
    try {
      if (editingId) {
        await axios.patch(`${API}/iptv-applications/${editingId}`, { name: form.name.trim(), active: form.active }, { headers: authHeaders() });
        toast.success("Application mise à jour");
      } else {
        await axios.post(`${API}/iptv-applications`, { name: form.name.trim(), active: form.active }, { headers: authHeaders() });
        toast.success("Application ajoutée");
      }
      reset();
      await loadApps();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Enregistrement application impossible"));
    } finally {
      setSaving(false);
    }
  };
  const editApp = (app) => { setEditingId(app.id); setForm({ name: app.name || "", active: app.active !== false }); };
  const toggleApp = async (app) => {
    try {
      await axios.patch(`${API}/iptv-applications/${app.id}`, { active: !app.active }, { headers: authHeaders() });
      toast.success(!app.active ? "Application activée" : "Application désactivée");
      await loadApps();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Mise à jour application impossible"));
    }
  };
  const deleteApp = async (app) => {
    if (!window.confirm(`Supprimer ${app.name} ?`)) return;
    try {
      await axios.delete(`${API}/iptv-applications/${app.id}`, { headers: authHeaders() });
      toast.success("Application supprimée");
      await loadApps();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Suppression impossible"));
    }
  };

  return (
    <section className="surface p-4" data-testid="iptv-applications-settings-section">
      <div className="flex flex-wrap items-start justify-between gap-3" data-testid="iptv-applications-settings-header">
        <div><h2 className="font-[Outfit] text-xl text-white" data-testid="iptv-applications-settings-title">Applications IPTV</h2><p className="mt-2 text-sm text-slate-400" data-testid="iptv-applications-settings-description">Gérez la liste searchable utilisée lors de la création ou modification d’un abonnement.</p></div>
        <button type="button" className="btn-secondary" onClick={loadApps} disabled={loading} data-testid="iptv-applications-refresh-button"><RefreshCw size={16} /> Actualiser</button>
      </div>
      <form className="mt-5 grid gap-3 sm:grid-cols-[minmax(0,1fr)_150px_auto]" onSubmit={submit} data-testid="iptv-application-form">
        <label className="field-label" htmlFor="iptv-application-name">Nom application<input id="iptv-application-name" className="field mt-2" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} data-testid="iptv-application-name-input" required /></label>
        <label className="field-label" htmlFor="iptv-application-active">Statut<select id="iptv-application-active" className="field mt-2" value={form.active ? "active" : "inactive"} onChange={(event) => setForm({ ...form, active: event.target.value === "active" })} data-testid="iptv-application-active-select"><option value="active">Actif</option><option value="inactive">Inactif</option></select></label>
        <div className="flex items-end gap-2"><button className="btn-primary w-full" disabled={saving} data-testid="iptv-application-submit-button"><Plus size={16} />{saving ? "Enregistrement..." : editingId ? "Mettre à jour" : "Ajouter"}</button>{editingId && <button type="button" className="btn-secondary" onClick={reset} data-testid="iptv-application-cancel-button">Annuler</button>}</div>
      </form>
      <div className="mt-5 overflow-x-auto" data-testid="iptv-applications-table-wrap"><table className="data-table" data-testid="iptv-applications-table"><thead><tr><th>Application</th><th>Statut</th><th>Utilisation</th><th>Actions</th></tr></thead><tbody>{loading ? <tr><td colSpan={4} data-testid="iptv-applications-loading">Chargement…</td></tr> : apps.length === 0 ? <tr><td colSpan={4}><div className="empty-state" data-testid="iptv-applications-empty"><p className="font-[Outfit] text-lg text-white">Aucune application</p></div></td></tr> : apps.map((app) => <tr key={app.id} data-testid={`iptv-application-row-${app.id}`}><td data-testid={`iptv-application-name-${app.id}`}>{app.name}</td><td><button type="button" className={app.active ? "badge badge-green" : "badge badge-neutral"} onClick={() => toggleApp(app)} data-testid={`iptv-application-toggle-${app.id}`}>{app.active ? "Actif" : "Inactif"}</button></td><td data-testid={`iptv-application-usage-${app.id}`}>{app.usage_count || 0}{app.recent && <span className="ml-2 badge badge-gold" data-testid={`iptv-application-recent-${app.id}`}>Récent</span>}</td><td><div className="flex flex-wrap gap-2"><button type="button" className="btn-secondary" onClick={() => editApp(app)} data-testid={`iptv-application-edit-${app.id}`}>Modifier</button><button type="button" className="btn-secondary" onClick={() => deleteApp(app)} data-testid={`iptv-application-delete-${app.id}`}><Trash2 size={15} /> Supprimer</button></div></td></tr>)}</tbody></table></div>
    </section>
  );
}